import { useEffect, useState } from 'react';
import { api, type BillEvent, type BillTimeline as Timeline } from '../lib/api';
import { ErrorBox, dash, money } from './DataTable';

/**
 * 청구 진행 창 — 거래처별 정산 「청구상태」 칸을 누르면(2026-10-19 way 캡처) 그 접수·월의
 * 청구 필요 → 청구 요청 → 발행(청구완료) → 발행 취소 / 발행 수정 → 입금 을 한 창에 시각·처리자·사유와 함께. 서버 `misuCancel.getBillTimeline`.
 * 아래 버튼은 이 칸에 원래 있던 것(청구 요청 · 청구 정보 = 취소·수정 승인요청) 그대로다.
 */
const STAGES = ['청구 필요', '청구 요청', '발행', '입금'] as const;
const TONE: Record<BillEvent['tone'], string> = {
  req: 'border-brand-300 bg-brand-50 text-brand-800',
  acct: 'border-sky-200 bg-sky-50 text-sky-800',
  issue: 'border-emerald-300 bg-emerald-50 text-emerald-800',
  cancel: 'border-red-300 bg-red-50 text-red-800',
  fix: 'border-violet-300 bg-violet-50 text-violet-800',
  paid: 'border-teal-300 bg-teal-50 text-teal-800',
  reject: 'border-orange-300 bg-orange-50 text-orange-800',
  muted: 'border-line bg-canvas text-ink-soft',
};

export default function BillTimeline({ recvId, driveMon, custName, onClose, onRequest, onInfo }: {
  recvId: number; driveMon: string; custName?: string | null; onClose: () => void;
  /** 「청구 요청」(청구 전·청구대상) — 없으면 버튼 없음. */
  onRequest?: (() => void) | null;
  /** 「청구 정보 · 취소·수정 승인요청」. */
  onInfo?: (() => void) | null;
}) {
  const [t, setT] = useState<Timeline | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [showMuted, setShowMuted] = useState(false);
  useEffect(() => {
    let alive = true;
    api.getBillTimeline({ recvId, driveMon }).then((x) => { if (alive) setT(x); }).catch((e) => { if (alive) setErr(e instanceof Error ? e.message : String(e)); });
    return () => { alive = false; };
  }, [recvId, driveMon]);

  const stageIdx = t ? STAGES.indexOf(t.stage) : -1;
  const events = (t?.events ?? []).filter((e) => showMuted || e.tone !== 'muted');
  const mutedN = (t?.events ?? []).filter((e) => e.tone === 'muted').length;

  return (
    <div className="card space-y-3 p-5 text-sm">
      <div className="flex flex-wrap items-center gap-2">
        <h2 className="text-base font-bold">청구 진행</h2>
        <span className="text-ink-soft">{dash(t?.custName ?? custName)} · 접수 {recvId}번 · {driveMon}{t?.billTotal ? ` · 청구액 ${money(t.billTotal)}원` : ''}</span>
        <button className="btn-ghost ml-auto" onClick={onClose}>닫기</button>
      </div>
      {err && <ErrorBox message={err} />}
      {!t && !err && <p className="text-ink-faint">불러오는 중…</p>}
      {t && (
        <>
          <ol className="flex flex-wrap items-center gap-1 text-xs">
            {STAGES.map((s, i) => (
              <li key={s} className="flex items-center gap-1">
                <span className={`rounded-full px-2.5 py-1 font-semibold ${i < stageIdx ? 'bg-emerald-100 text-emerald-800' : i === stageIdx ? 'bg-brand-600 text-white' : 'bg-canvas text-ink-faint'}`}>
                  {i < stageIdx ? '✓ ' : ''}{s}
                </span>
                {i < STAGES.length - 1 && <span className="text-ink-faint">→</span>}
              </li>
            ))}
            {t.pending && (
              <li className="ml-2 rounded-full bg-amber-100 px-2.5 py-1 font-semibold text-amber-800">
                {t.pending.kind} 요청 회계 확인 대기
              </li>
            )}
          </ol>
          {t.misu && (
            <p className="text-xs text-ink-soft">
              지금: 청구상태 <b>{t.misu.req_status || '없음'}</b>{t.misu.req_call_date ? ` · 청구요청일 ${t.misu.req_call_date}` : ''}
              {t.misu.req_date ? ` · 청구일 ${t.misu.req_date}` : ''} · 입금상태 <b>{t.misu.bank_status || '없음'}</b>
              {t.misu.deposit_date ? ` · 입금일 ${t.misu.deposit_date}` : ''}
            </p>
          )}
          {events.length === 0 ? (
            <p className="rounded-lg border border-dashed border-line px-3 py-4 text-center text-ink-faint">
              아직 청구 기록이 없습니다 — 「청구 요청」으로 회계팀에 올리면 여기부터 쌓입니다.
            </p>
          ) : (
            <ol className="max-h-[55vh] space-y-1.5 overflow-y-auto border-l-2 border-line pl-3">
              {events.map((e, i) => (
                <li key={i} className="relative">
                  <span className="absolute -left-[1.15rem] top-2 h-2.5 w-2.5 rounded-full border-2 border-surface bg-ink-faint" />
                  <div className={`rounded-lg border px-3 py-1.5 ${TONE[e.tone]}`}>
                    <div className="flex flex-wrap items-baseline gap-x-2">
                      <span className="text-[11px] font-semibold opacity-70">{e.step}</span>
                      <b>{e.title}</b>
                      <span className="ml-auto whitespace-nowrap text-xs tabular-nums opacity-80">{e.at.slice(0, 16).replace('T', ' ')}{e.who ? ` · ${e.who}` : ''}</span>
                    </div>
                    {e.detail && <div className="mt-0.5 whitespace-pre-wrap break-keep text-xs">{e.detail}</div>}
                  </div>
                </li>
              ))}
            </ol>
          )}
          {mutedN > 0 && (
            <label className="flex items-center gap-1.5 text-xs text-ink-soft">
              <input type="checkbox" checked={showMuted} onChange={(e) => setShowMuted(e.target.checked)} />
              청구상태 변경 이력도 보기({mutedN}건)
            </label>
          )}
        </>
      )}
      {(onRequest || onInfo) && (
        <div className="flex flex-wrap justify-end gap-2 border-t border-line pt-3">
          {onInfo && <button className="btn-ghost" onClick={onInfo}>청구 정보 · 취소·수정 승인요청</button>}
          {onRequest && <button className="btn-primary" onClick={onRequest}>청구 요청</button>}
        </div>
      )}
    </div>
  );
}
